import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Ticket } from 'types/TicketsState';
import {
  dateFormatForCalendar,
  getTicketsForSpecificDate,
} from 'utils/calendar';
import { getEventSlug } from 'utils/events';
import { buildPriceToShow, getTaxPrice } from 'utils/tickets';
import {
  makeSelectedEvent,
  selectTicketSaleIsLoading,
} from '../TicketSalePage/slice/selectors';
import { createChatBaseCart } from '../TicketSalePage/slice/thunkActions';
import {
  EventCalendar,
  EventReservedTickets,
} from '../TicketSalePage/slice/types';
import { useEffect, useState } from 'react';
import { insertChatBaseScript } from './insertChatBaseScript';
import { selectVendorData } from 'store/vendorSlice/selectors';
import { toast } from 'react-toastify';
import { toastOptions } from 'utils/static/constants';
import { getTicketAvailabilityOnCalendar } from 'app/components/TicketSection/utils';

const useChatBase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectTicketSaleIsLoading);
  const event = useSelector(makeSelectedEvent);
  const {
    id,
    name,
    calendar,
    reservedTickets,
    tickets,
    currency,
    tax_included,
    tax_rate,
  } = event;
  const { features } = useSelector(selectVendorData);
  const [isChatBaseScriptInserted, setIsChatBaseScriptInserted] =
    useState(false);

  useEffect(() => {
    if (!isLoading && !isChatBaseScriptInserted && features.hasAISalesAgent) {
      insertChatBaseScript();
      setIsChatBaseScriptInserted(true);

      (window as any).chatbase.registerTools({
        get_initial_data: ({ attendanceDate }: { attendanceDate: string }) => {
          console.log('get_initial_data args ---', { attendanceDate });
          try {
            const convertedAttendanceDate = DateTime.fromFormat(
              attendanceDate,
              dateFormatForCalendar,
            );
            const ticketsForSelectedDate = getTicketsForSpecificDate(
              calendar as EventCalendar,
              convertedAttendanceDate,
            ).map(
              element =>
                tickets.find(
                  ticket => ticket.id === element.ticketTypeId,
                ) as Ticket,
            );

            const formattedTickets = ticketsForSelectedDate.map(ticket => {
              const firstTicketOption = ticket.ticketprice[0];
              let thePricePaidForASingleTicket = firstTicketOption?.price;

              if (tax_included) {
                const theTaxPaidForASingleTicket = getTaxPrice(
                  firstTicketOption?.price,
                  tax_rate.rate,
                );

                thePricePaidForASingleTicket += theTaxPaidForASingleTicket;
              }

              return {
                id: ticket.id,
                name: ticket.name,
                price: thePricePaidForASingleTicket,
                availableQuantity: getTicketAvailabilityOnCalendar(
                  calendar as EventCalendar,
                  reservedTickets as EventReservedTickets,
                  ticket.id,
                  convertedAttendanceDate,
                ),
                addons: ticket.ticketaddons.map(({ id, name, price }) => ({
                  id,
                  name,
                  price: buildPriceToShow(price, currency, t('event.free')),
                })),
              };
            });

            console.log('formattedTickets ---', formattedTickets);

            return {
              status: 'success',
              data: formattedTickets,
            };
          } catch (error: any) {
            // Return only the error message without any data
            console.log(error);
            return { status: 'error', message: error.message };
          }
        },
        create_cart_and_redirect: async (args: any) => {
          console.log('create_cart_and_redirect args ---', args);
          try {
            const chatBaseSelectedItems = JSON.parse(args.selectedItems);

            await dispatch(
              createChatBaseCart(chatBaseSelectedItems, args.attendanceDate),
            );

            navigate(
              `/event/${getEventSlug(
                name,
                id,
              )}/buy-tickets/billing-information`,
            );

            (window as any).chatbase.close();

            toast.success(
              `Complete your billing info, click the button “Pay” at the bottom, and you are done! Thank you for coming and have fun! 🎉`,
              toastOptions,
            );

            return { status: 'success', data: null };
          } catch (error: any) {
            // Return only the error message without any data
            console.log(error);
            return { status: 'error', message: error.message };
          }
        },
      });
    }
  }, [isLoading]);
};

export default useChatBase;
